import { useNavigate, useParams } from 'react-router-dom';
import ImportedStyles from '../NodeTerminal/NodeTerminal.module.scss';
import Styles from './NodeSets.module.scss';
import { useState, useRef, useEffect } from 'react';
import { useContext } from 'react';

// module imports
import _ from 'lodash';
import { ToastContainer, toast } from 'react-toastify';
import CalculateIcon from '@mui/icons-material/Calculate';

import {
	Avatar, Typography, Tabs, Tab,
	AppBar, TextField, Select, MenuItem, FormControl
} from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { amber } from '@mui/material/colors';
import axios from 'axios';

// component imports
import { AuthContext, ViewContext } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import { NodeTimeline } from 'components/global/NodeLayout/NodeTimeline';
import ChartVenn from 'components/global/NodeLayout/SetComponents/VennDiagram';
import CartesianProduct from 'components/global/NodeLayout/SetComponents/CartesianProduct';
import RelationsSet from 'components/global/NodeLayout/SetComponents/RelationsSet';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import NodeProgress from 'components/global/NodeLayout/NodeProgress/NodeProgress';
import { Joyride } from 'components/walkthrough/walkthrough';
import { useAPI, Feedback } from 'components/lib';
import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';

// asset imports
import Soundcheck from 'assets/sounds/try-again.mp3';
import { decoder } from 'utils/helpers/decoder';
import { HelperChat } from 'components/global/HelperChat/HelperChat';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';
import { InstructionPane } from 'components/global/InstructionPane/InstructionPane';
import { copyEnabledChecker } from 'utils/helpers/utility';

const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;

export const SetOperations = ({ value, setValue, t }) => {

	return (
		<div>
			<FormControl error={false}>
				<Select
					id='httpMethodSelector'
					value={value}
					onChange={setValue}
					IconComponent={() => null}
					displayEmpty
					MenuProps={{
						disableScrollLock: true
					}}
					renderValue={(selected) => {
						switch (selected) {
							case 'union':
								return '∪';
							case 'intersection':
								return '∩';
							case 'difference':
								return '-';
							case 'cartesian':
								return '×';
							case 'relation':
								return 'R';
							default:
								return '';
						}
					}}
					sx={{
						height: '40px',
						flexWrap: 'wrap',
						alignContent: 'center',
						// justifyContent: 'space-around',
						width: '40px',
						borderRadius: '8px',
						bgcolor: 'ghostwhite',
						// color: 'red'
					}}
				>
					<MenuItem value="">{t('Labs.Nodes.NodeSets.operations.none')}</MenuItem>
					<MenuItem value="union">∪</MenuItem>
					<MenuItem value="intersection">∩</MenuItem>
					<MenuItem value="difference">-</MenuItem>
					<MenuItem value="cartesian">×</MenuItem>
					<MenuItem value="relation">R</MenuItem>
				</Select>
			</FormControl>
		</div>
	)
}

export function NodeSets(props) {
	const { lab_id, node_id } = useParams();
	const { resource } = useParams();

	const [steps, setSteps] = useState();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [nodeFinished, setNodeFinished] = useState(false);
	const [isMuted, setIsMuted] = useState(true);
	const [currentStep, setCurrentStep] = useState(0);
	const [reqView, setReqView] = useState(0);
	const [operationOne, setOperationOne] = useState('union');
	const [inputSetA, setInputSetA] = useState('1, 2, 3, 4, 5');
	const [inputSetB, setInputSetB] = useState('5, 6, 7, 8, 9');
	const [vennData, setVennData] = useState({
		data: [
			{ label: "A", values: [1, 2, 3, 4, 5] },
			{ label: "B", values: [5, 6, 7, 8, 9] },
		],
		diagramLabel: props.t('Labs.Nodes.NodeSets.label.walkthrough')
	});
	const [startLab, setStartLab] = useState(true);
	const [lines, setLines] = useState([]);
	const [nodeInfo, setNodeInfo] = useState();
	const [displayData, setDisplayData] = useState();

	const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');
	const context = useContext(AuthContext);
	const [showFinishConfirm, setShowFinishConfirm] = useState(false);
	const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);
	const [maxUnlocked, setMaxUnlocked] = useState(0);
	const audioRef = useRef(null);
	const finalAudioRef = useRef(null);
	const viewContext = useContext(ViewContext)
	const isCopyEnabled = copyEnabledChecker(context?.user)
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		setMaxUnlocked(prev => Math.max(prev, currentStep));
	}, [currentStep]);

	useEffect(() => {
		if (data) {
			const jsonData = decoder(data);
			jsonData.numOfSteps = jsonData.steps.steps.length;
			setNodeInfo(jsonData);
			setSteps(jsonData.steps.steps);
			document.title = props.t('Labs.Nodes.NodeSets.title', { nodeName: jsonData.name })
			setDisplayData(jsonData.content.reference_data);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data])

	useEffect(() => {
		if (!steps) return;

		if (audioRef.current) {
			audioRef.current.pause();
		}

		const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
		if (audioLink && !isMuted) {
			const newAudio = new Audio(audioLink);
			audioRef.current = newAudio;
			newAudio.play();
		}

		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, isMuted]);

	useEffect(() => {
		if (!nodeFinished) return;

		if (audioRef.current) {
			audioRef.current.pause();
		}

		const audio = new Audio(finalAudio);
		finalAudioRef.current = audio;
		audio.play();

		viewContext.notification.show(
			props.t('Labs.Nodes.NodeSets.end'),
			'info',
			false
		);

		return () => {
			if (finalAudioRef.current) {
				finalAudioRef.current.pause();
			}
		};

	}, [nodeFinished])

	const navigate = useNavigate();

	const navigateBack = () => {
		navigate(-1);
	};

	// confrimation card confirm button
	const startLabClick = async () => {
		const options = {
			method: 'POST',
			url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
			headers: {
				accept: 'application/json',
			},
		};
		await axios
			.request(options)
			.then((response) => {
				new Promise((resolve) => setTimeout(resolve, 100));
				setStartLab(false);
				if (context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id)) {
					setIsMuted(!isMuted)
					setVennData({
						data: [],
						diagramLabel: props.t('Labs.Nodes.NodeSets.label.default')
					});
					setInputSetA('')
					setInputSetB('')
					setOperationOne('')
				}
			})
			.catch((err) => {
				if (err.response.status === 403) {
					navigate('/nopermission');
				} else {
					navigate('/oops');
				}
			});
	};

	const handleVoiceClick = () => {
		setIsMuted(!isMuted);
	};

	const updateVennData = (newData, newLabel) => {
		setVennData({
			data: newData,
			diagramLabel: newLabel
		});
	};

	const handleOperationOneChange = (event) => {
		setOperationOne(event.target.value);
	};

	const currentRef = useRef(null);

	const handleDrawer = () => {
		setDrawerOpen(!drawerOpen);
	};

	const handleReqViewChange = (event, newValue) => {
		setReqView(newValue);
	};

	const inputStyles = {
		height: '40px',
		borderRadius: '8px',
		width: '60%',
		bgcolor: 'ghostwhite'
	}

	const handleNext = () => {
		const options = {
			method: "POST",
			url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
			headers: {
				"accept": "application/json",
			},
			...(nodeInfo.is_assessment === 1 && { data: { completionPercent: roundedPercentage } }),
		};
		axios
			.request(options)
			.then(async function (response) {
				await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
				if (nodeInfo.is_assessment) {
					navigate(`/labs/${lab_id}/nodes`)
				} else {
					navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
				}
			})
			.catch((err) => {
				let status = err.response.status;
				console.log("error", status);
				navigate('/oops');
			});
	};

	function extractValuesFromCSV(csvString) {
		const valuesArray = csvString.split(',');

		const valuesSet = new Set(valuesArray.map(value => value.trim()).filter(value => value !== ''));
		const filteredValuesList = Array.from(valuesSet);

		return filteredValuesList;
	}

	function subtractSets(setA, setB, operation) {

		if (operation === 'intersection') {
			return setA.filter(element => setB.includes(element));
		} else if (operation === 'difference') {
			return setA.filter(element => !setB.includes(element));
		}

		return [];
	}

	function moveToNext() {
		if (currentStep !== nodeInfo.numOfSteps - 1) {
			setCurrentStep(currentStep + 1);
			var myElement = document.getElementById('currenttimelineHolder');
			var topPos = myElement.offsetTop;
			document.getElementById('timelinescrolldiv').scrollTop = topPos;
			toast.dark(props.t('Labs.Nodes.NodeSets.toast.correct'), {
				type: 'success',
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				autoClose: 3000
			});
		} else {
			setNodeFinished(true);
		}
	}

	function moveToErrorVerbose(item, expectedState = props.t('Labs.Nodes.NodeSets.none')) {
		const wastedAttemptAudio = new Audio(Soundcheck);
		wastedAttemptAudio.play();
		toast.dark(props.t('Labs.Nodes.NodeSets.toast.incorrect',
			{ item: item, expectedState: expectedState }), {
			type: 'error',
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
		return;
	}

	function validateSets(listA, listB) {
		const expectedObjects = steps[currentStep].expected_input;
		const thingsToCheck = Object.keys(expectedObjects);

		let [setAValidity, setBValidity, operationValidity, relationValidtiy] = [true, true, true, true];

		if (thingsToCheck.includes('setA')) {
			setAValidity = _.isEqual(_.sortBy(listA), _.sortBy(expectedObjects.setA.map(String)))
			setAValidity || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.setB'), expectedObjects.setA, listA);
		} else {
			setAValidity = listA.length === 0
			setAValidity || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.setB'), expectedObjects.setA, listA);
		}

		if (thingsToCheck.includes('setB')) {
			setBValidity = _.isEqual(_.sortBy(listB), _.sortBy(expectedObjects.setB.map(String)))
			setBValidity || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.setB'), expectedObjects.setB, listB);
		} else {
			setBValidity = listB.length === 0
			setBValidity || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.setB'), expectedObjects.setB, listB);
		}

		if (thingsToCheck.includes('operation')) {
			operationValidity = expectedObjects.operation === operationOne
			operationValidity || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.operation'), expectedObjects.operation, operationOne);
		} else {
			operationValidity = '' === operationOne
			operationValidity || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.operation'), expectedObjects.operation, operationOne);
		}

		if (thingsToCheck.includes('relation')) {
			const relations = lines.map(entry => entry.elements);
			relationValidtiy = _.isEqual(_.sortBy(expectedObjects.relation), _.sortBy(relations.map(String))); relationValidtiy || moveToErrorVerbose(props.t('Labs.Nodes.NodeSets.items.relation'), expectedObjects.relation, relations);
		} else {
			relationValidtiy = true
		}

		if (setAValidity && setBValidity && operationValidity && relationValidtiy) {
			moveToNext()
		}
	}


	function updateDiagram() {

		const listA = extractValuesFromCSV(inputSetA);
		const listB = extractValuesFromCSV(inputSetB);

		validateSets(listA, listB)

		if (operationOne === 'union') {
			updateVennData(
				[
					{ "label": "A ∪ B", values: extractValuesFromCSV(inputSetA + ',' + inputSetB) }
				],
				props.t('Labs.Nodes.NodeSets.label.union')
			);
			setReqView(0);
			return;
		}

		if (operationOne === 'intersection') {
			updateVennData(
				[
					{ "label": "A ∩ B", values: subtractSets(listA, listB, 'intersection') }
				],
				props.t('Labs.Nodes.NodeSets.label.intersection')
			);
			setReqView(0);
			return;
		}

		if (operationOne === 'difference') {
			updateVennData(
				[
					{ "label": "A - B", values: subtractSets(listA, listB, 'difference') }
				],
				props.t('Labs.Nodes.NodeSets.label.difference')
			);
			setReqView(0);
			return;
		}

		if (inputSetA !== '' || inputSetB !== '') {
			const setData = [];

			if (inputSetA !== '') {
				setData.push({ label: "A", values: listA });
			}

			if (inputSetB !== '') {
				setData.push({ label: "B", values: listB });
			}
			setReqView(0);
			updateVennData(setData, props.t('Labs.Nodes.NodeSets.label.default'));
		}

		if (operationOne === 'relation') {
			setReqView(1);
		}

		if (operationOne === 'cartesian') {
			setReqView(2);
		}
	}

	// If the set is changed then the relations are cleared
	useEffect(() => {
		if (lines.length) {
			setLines([])
			toast.dark(`Relations reset!`, {
				type: 'info',
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inputSetA, inputSetB])

	const resetWalkthrough = (data) => {
		if (data.status === 'finished' | data.action === 'skip' | data.action === 'tour:end') {
			setIsMuted(!isMuted)
			setVennData({
				data: [],
				diagramLabel: props.t('Labs.Nodes.NodeSets.label.default')
			});
			setInputSetA('')
			setInputSetB('')
			setOperationOne('')
		}
	}

	useEffect(() => {
		const resourceIncluded = context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id);
		if (resourceIncluded && !startLab) {
			setIsMuted(!isMuted)
			setVennData({
				data: [],
				diagramLabel: props.t('Labs.Nodes.NodeSets.label.default')
			});
			setInputSetA('')
			setInputSetB('')
			setOperationOne('')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [context?.user?.stop_walkthrough])

	const showConfirm = () => {
		setShowFinishConfirm(true);
	}

	const handleConfirmClick = () => {
		handleNext();
	}

	const handleCopy = async (e) => {
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
		}, 2000);
		toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
			type: 'warning',
			position: 'top-right',
			autoClose: 3000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	};

	const handleCopyDisabled = (e) => {
		e.preventDefault();
	}

	return (
		<>
			<ToastContainer
				id="warning-toast"
				position="top-left"
				autoClose={10000}
				hideProgressBar={false}
				// newestOnTop
				closeOnClick
				pauseOnFocusLoss
				draggable
				pauseOnHover
				limit={3}
			/>
			<BackConfirmationCard
				isSaveAvailable={true}
			/>
			<div id='ancestorContainer' className={ImportedStyles.ancestorContainer}>
				{
					loading ? (
						<div>
							<LoadingBackdrop isOpen={true} />
						</div>
					) : (
						steps ? (
							<>
								<ConfirmationCard
									title={nodeInfo.is_assessment ?
										props.t('Labs.Nodes.NodeSets.confirm.start.assessment.title') :
										props.t('Labs.Nodes.NodeSets.confirm.start.default.title')
									}
									body={nodeInfo.is_assessment ?
										props.t('Labs.Nodes.NodeSets.confirm.start.assessment.body') :
										props.t('Labs.Nodes.NodeSets.confirm.start.default.body')
									}
									showCard={startLab}
									setShowCard={setStartLab}
									handleCancel={navigateBack}
									handleConfirm={startLabClick}
								/>
								{!startLab && nodeInfo.is_assessment !== 1 ?
									<Joyride
										open={nodeInfo?.resource?.walkthrough?.open}
										run={nodeInfo?.resource?.walkthrough?.run}
										steps={nodeInfo?.resource?.walkthrough?.steps || []}
										resourceId={nodeInfo?.resource?.lab_resource_id}
										disableOverlay={true}
										callback={resetWalkthrough}
									/>
									: null}
								<div
									id="titleContainer"
									className={ImportedStyles.titleContainer}>
									<div style={{ display: 'flex' }}>
										<Avatar
											sx={{
												bgcolor: amber[500],
												alignSelf: 'center',
												mr: 1,
												width: 55,
												height: 55,
											}}
										>
											<CalculateIcon sx={{ width: 42, height: 42 }} />
										</Avatar>
										<Typography
											variant="h3"
											sx={{
												textTransform: 'uppercase',
												color: '#FFFFFF',
											}}
										>
											{nodeInfo.name}
										</Typography>
									</div>


									<span style={{ display: 'flex', flexDirection: 'row' }}>
										<LightTooltip
											title={nodeInfo.is_assessment
												? ((roundedPercentage > 70) || nodeFinished ?
													props.t('Labs.Nodes.NodeSets.tooltip.finish.done')
													: props.t('Labs.Nodes.NodeSets.tooltip.finish.assessment')
												)
												: (nodeFinished ?
													props.t('Labs.Nodes.NodeSets.tooltip.finish.done')
													: props.t('Labs.Nodes.NodeSets.tooltip.finish.info')
												)
											}
											arrow
											placement="left"
											classes={{
												tooltip: {
													backgroundColor: '#fff',
													color: 'rgba(0, 0, 0, 0.87)',
													boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
													fontSize: 11,
												},
											}}
										>
											<span>
												<button
													variant="extended"
													className={Styles.finishButton}
													onClick={() => {
														nodeInfo.is_assessment ? showConfirm() : handleNext()
													}}
													disabled={
														!nodeInfo.is_assessment
															? !nodeFinished
															: roundedPercentage < 70
													}
												>
													<KeyboardDoubleArrowRightIcon />
													{props.t('Labs.Nodes.NodeSets.button.finish')}
												</button>
											</span>
										</LightTooltip>
										<ConfirmationCard
											title={(roundedPercentage > 70 && roundedPercentage < 100) ?
												props.t('Labs.Nodes.NodeSets.confirm.completed.title.warn')
												: props.t('Labs.Nodes.NodeSets.confirm.completed.title.default')
											}
											body={(roundedPercentage > 70 && roundedPercentage < 100) ?
												props.t('Labs.Nodes.NodeSets.confirm.completed.body.warn')
												: props.t('Labs.Nodes.NodeSets.confirm.completed.body.default')
											}
											showCard={showFinishConfirm}
											setShowCard={setShowFinishConfirm}
											handleConfirm={handleConfirmClick}
										/>
										<NodeProgress
											lab_id={lab_id}
											node_id={node_id}
											currentStep={currentStep}
											setDrawerOpen={setDrawerOpen}
											drawerOpen={drawerOpen}
											setCurrentStep={setCurrentStep}
											initialUserProgress={nodeInfo.userProgress}
											steps={steps}
											data={data}
											resource={nodeInfo?.resource}
											maxUnlocked={maxUnlocked}
											setMaxUnlocked={setMaxUnlocked}
										/>
									</span>
								</div>
								<div id='midContainer' className={`${ImportedStyles.midContainer} ${Styles.midContainer}`}>
									<div id='lessonContainer' className={Styles.lessonContainer}>
										<div id='setsContainer' className={Styles.setsContainer}>
											<TextField
												value={inputSetA}
												id="setAInput"
												placeholder={props.t('Labs.Nodes.NodeSets.placeholder.setA')}
												size="small"
												onChange={(e) => setInputSetA(e.target.value)}
												sx={inputStyles}
												InputProps={{
													// readOnly: reqView === 1,
													style: { color: 'black' },
													startAdornment: (
														<>
															<span className={Styles.setAdornStyle}>
																{'A: {'}
															</span>
															<span className={Styles.setAdornBracketStyle}>
																{'}'}
															</span>
														</>
													)
												}}
											/>
											<SetOperations
												value={operationOne}
												setValue={handleOperationOneChange}
												t={props.t}
											/>
											<TextField
												value={inputSetB}
												id="setBInput"
												placeholder={props.t('Labs.Nodes.NodeSets.placeholder.setB')}
												size="small"
												// fullWidth
												onChange={(e) => setInputSetB(e.target.value)}
												sx={inputStyles}
												InputProps={{
													// readOnly: reqView === 1,
													style: { color: 'black' },
													startAdornment: (
														<>
															<span className={Styles.setAdornStyle}>
																{'B: {'}
															</span>
															<span className={Styles.setAdornBracketStyle}>
																{'}'}
															</span>
														</>
													)
												}}
											/>
											<button
												id='updateButton'
												className={Styles.updateButton}
												onClick={() => { updateDiagram() }}
											>
												{props.t('Labs.Nodes.NodeSets.button.update')}
											</button>
										</div>
										<div id='setViewContainer' className={Styles.setViewContainer}>
											<AppBar
												id='viewSelector'
												position="static"
												sx={{
													bgcolor: 'transparent',
													borderRadius: '8px',
													boxShadow: 'none'
												}}
											>
												<Tabs
													id='selectorTabs'
													value={reqView}
													onChange={handleReqViewChange}
													variant="fullWidth"
													TabIndicatorProps={{
														sx: {
															backgroundColor: '#FFC107',
															height: 3,
														}
													}}
												>
													<Tab
														id="vennDiagram"
														label={props.t('Labs.Nodes.NodeSets.label.diagram')}
														sx={{
															width: '30px',
															color: 'slategrey',
															transition: 'color 0.5s ease-in-out',
															'&.Mui-selected': {
																color: 'black',
															},
														}}
													/>
													<Tab
														id="relations"
														label={props.t('Labs.Nodes.NodeSets.label.relations')}
														sx={{
															width: '30px',
															color: 'slategrey',
															transition: 'color 0.5s ease-in-out',
															'&.Mui-selected': {
																color: 'black',
															},
														}}
													/>
													<Tab
														id="cartesianProd"
														label={props.t('Labs.Nodes.NodeSets.label.cartesian')}
														sx={{
															width: '30px',
															color: 'slategrey',
															transition: 'color 0.5s ease-in-out',
															'&.Mui-selected': {
																color: 'black',
															},
														}}
													/>
												</Tabs>
											</AppBar>
										</div>
										<div id='dataSetDisplay' className={Styles.dataSetDisplay}>
											{reqView === 0 &&
												<ChartVenn
													vennData={vennData}
												/>
											}
											{reqView === 1 &&
												<RelationsSet
													setData={[
														extractValuesFromCSV(inputSetA),
														extractValuesFromCSV(inputSetB)
													]}
													lines={lines}
													setLines={setLines}
													toast={toast}
													selectedOperation={operationOne}
												/>
											}
											{reqView === 2 &&
												<CartesianProduct
													setData={[
														extractValuesFromCSV(inputSetA),
														extractValuesFromCSV(inputSetB)
													]}
													selectedOperation={operationOne}
													relations={lines}
												/>
											}
										</div>
									</div>
									<div data-testid="nodeSets-midContainer-instructionsContainer" id="instructionsContainer" className={ImportedStyles.instructionsContainer}>
										<InstructionPane
											props={props}
											steps={steps}
											currentStep={currentStep}
											isMuted={isMuted}
											setIsMuted={setIsMuted}
											onHandleVoiceClick={handleVoiceClick}
											copied={copied}
											showLineNumbers={false}
											onHandleCopy={handleCopy}
											onHandleCopyDisabled={handleCopyDisabled}
											nodeInfo={nodeInfo}
											nodeSet={true}
											theme="light"
											isCopyEnabled={isCopyEnabled}
											displayCopyButton={false}
										/>
									</div>
									<div data-testid="nodeSets-midContainer-Timeline" id="Timeline" className={Styles.Timeline}>
										<NodeTimeline
											nodeFinished={nodeFinished}
											currentStep={currentStep}
											steps={steps}
											currentRef={currentRef}
											hideToolTip={nodeInfo.is_assessment === 1}
											collapsable={true}
											setCurrentStep={setCurrentStep}
											maxUnlocked={maxUnlocked}
										/>
									</div>
								</div>
								<InfoDrawer
									handleDrawer={handleDrawer}
									drawerOpen={drawerOpen}
									titleText={props.t('Labs.Nodes.NodeSets.reference', { LabName: nodeInfo.labName })}
									displayData={displayData}
								/>
								{(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
									<HelperChat
										active={props.aiChat}
										topic={nodeInfo?.resource?.config?.chatbotTopic}
									/>
								)}
								{nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}

							</>
						) : (
							<div>
								{props.t('Labs.Nodes.NodeSets.error')}
							</div>
						)
					)}
			</div>
		</>
	);
}
